//-- Copyright (c) 2021 TMT Insights Group, LLC
import styled from '@emotion/styled';
import { useDroppable } from '@dnd-kit/core';
import { ColumnDirectory, ColumnDirectoryItem, SectionAddress } from './ColumnSelector';
import DraggableColumn from './DraggableColumn';
import { Alert } from '@mui/material';
import {
  SortableContext,
  verticalListSortingStrategy
} from "@dnd-kit/sortable";

interface DroppableSectionProps {
  title: string;
  data: ColumnDirectoryItem[];
  sectionKey: string;
  checkCallback: ({ section, index }: SectionAddress, newToggleValue: boolean) => void;
}

const DroppableSection = ({ title, sectionKey, checkCallback, data }: DroppableSectionProps) => {
  const { setNodeRef, over } = useDroppable({
    id: sectionKey,
  });
  const isOverContainer = over?.data?.current?.sortable?.containerId === sectionKey || over?.id === sectionKey;

  return (
    <SortableContext
      id={sectionKey}
      items={data.map((item) => item.key)}
      strategy={verticalListSortingStrategy}
    >
      <Wrapper ref={setNodeRef}>
        <Title>{title}</Title>
        <ScrollableDiv>
          <ColumnList>
            {!data?.length ? (
              <Alert sx={{ opacity: isOverContainer ? 0.5 : 1 }} severity="info">
                {sectionKey === 'available' ? 'All available columns are in use' : 'Minimum of one column required'}
              </Alert>
            ) : (
              data.map(({ checked, key, label }, relativeIndex) => (
                <DraggableColumn
                  sectionKey={sectionKey}
                  handleChange={(index: number, checked: boolean) =>
                    checkCallback({ section: sectionKey as keyof ColumnDirectory, index: index }, checked)
                  }
                  checked={checked}
                  key={key}
                  id={key}
                  label={label}
                  index={relativeIndex}
                />
              ))
            )}
          </ColumnList>
        </ScrollableDiv>
      </Wrapper>
    </SortableContext>
  );
};

export default DroppableSection;

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const ColumnList = styled.div`
  display: flex;
  flex-direction: column;
  flex-wrap: nowrap;
  height: fit-content;
`;

const ScrollableDiv = styled.div`
  overflow-y: auto;
  overflow-anchor: none;
  min-height: 200px;
  max-height: 60vh;
  margin-bottom: ${({ theme }) => theme.spacing(1)};
`;

const Title = styled.h3`
  opacity: 0.5;
  margin-top: 0px;
  font-weight: ${({ theme }) => theme.typography.fontWeightBold};
`;
